import React, { useState, useEffect, useRef } from 'react';
import { quizService } from '../services/quizService';
import { saveQuizAttempt } from '../firebase';
import { useAuth } from './Auth/AuthProvider';
import logger from '../utils/logger';
import QuizQuestion from './QuizQuestion';
import QuestionResult from './QuestionResult';
import QuizResults from './QuizResults';
import './Quiz.css';

const Quiz = ({ quizId, userId, onBack, onRestartWithSettings, quizSettings, isSharedQuiz = false, originalUserId = null }) => {
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [, setAllQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [shuffledChoices, setShuffledChoices] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [showingResult, setShowingResult] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startTime] = useState(new Date());
  const questionStartRef = useRef(null);

  useEffect(() => {
    logger.componentMount('Quiz');
    logger.userAction('start_quiz', { quizId, userId });
    
    const fetchQuizData = async () => {
      try {
        setLoading(true);
        logger.startTimer('quiz_load');
        
        // Fetch quiz info and questions
        const [quizData, questionsData] = await Promise.all([
          quizService.getQuiz(userId, quizId),
          quizService.getQuizQuestions(userId, quizId)
        ]);
        
        setQuiz(quizData);
        setAllQuestions(questionsData);
        
        // Use new question selection logic with presentation count
        const selectedQuestions = await quizService.selectQuestionsWithPresentationCount(
          userId, 
          quizId, 
          questionsData, 
          quizSettings
        );
        
        setQuestions(selectedQuestions);
        
        // Shuffle choices for each question
        const shuffledChoicesMap = {};
        selectedQuestions.forEach(question => {
          if (question?.id && question?.choices) {
            shuffledChoicesMap[question.id] = quizService.shuffleArray(question.choices);
          }
        });
        setShuffledChoices(shuffledChoicesMap);
        
        // Initialize user answers
        setUserAnswers(new Array(selectedQuestions.length).fill(null));
        
        const loadDuration = logger.endTimer('quiz_load');
        logger.info(`Quiz loaded successfully`, { 
          quizId,
          quizTitle: quizData.title,
          totalQuestions: questionsData.length,
          selectedQuestions: selectedQuestions.length,
          orderMode: quizSettings.orderMode,
          loadDuration 
        }, 'COMPONENT');
        
      } catch (err) {
        setError('クイズの読み込みに失敗しました');
        logger.error('Failed to load quiz', { 
          quizId,
          userId,
          error: err.message,
          stack: err.stack 
        }, 'COMPONENT');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizData();
    
    return () => {
      logger.componentUnmount('Quiz');
    };
  }, [quizId, userId, quizSettings]);

  const handleAnswer = (selectedChoiceIds) => {
    const newAnswers = [...userAnswers];
    const currentQuestion = questions[currentQuestionIndex];
    
    // Check if answer is correct
    const correctChoiceIds = currentQuestion.choices
      .filter(choice => choice.isCorrect)
      .map(choice => choice.id);
    
    const isCorrect = 
      selectedChoiceIds.length === correctChoiceIds.length &&
      selectedChoiceIds.every(id => correctChoiceIds.includes(id));
    
    newAnswers[currentQuestionIndex] = {
      questionId: currentQuestion.id,
      shuffledChoiceOrder: shuffledChoices[currentQuestion.id]?.map(choice => choice.id) || [],
      selectedChoiceIds,
      isCorrect
    };
    
    setUserAnswers(newAnswers);
    
    logger.userAction('answer_question', {
      quizId,
      questionId: currentQuestion.id,
      questionIndex: currentQuestionIndex + 1,
      selectedChoices: selectedChoiceIds,
      isCorrect,
      questionType: currentQuestion.questionType
    });

    // Show result immediately after answer
    setShowingResult(true);
  };

  const handleNextFromResult = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowingResult(false);
      logger.userAction('navigate_next_question', {
        quizId,
        fromQuestion: currentQuestionIndex + 1,
        toQuestion: currentQuestionIndex + 2
      });
      // Scroll to question start when moving to next question
      setTimeout(() => {
        questionStartRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      logger.userAction('complete_quiz_attempt', { quizId });
      completeQuiz();
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      logger.userAction('navigate_next_question', {
        quizId,
        fromQuestion: currentQuestionIndex + 1,
        toQuestion: currentQuestionIndex + 2
      });
      // Scroll to question start when moving to next question
      setTimeout(() => {
        questionStartRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      logger.userAction('complete_quiz_attempt', { quizId });
      completeQuiz();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      logger.userAction('navigate_previous_question', {
        quizId,
        fromQuestion: currentQuestionIndex + 1,
        toQuestion: currentQuestionIndex
      });
      // Scroll to question start when moving to previous question
      setTimeout(() => {
        questionStartRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const completeQuiz = async () => {
    try {
      const endTime = new Date();
      const duration = endTime - startTime;
      const score = quizService.calculateScore(userAnswers);
      
      const attemptData = {
        userId: user.uid,
        startedAt: startTime.toISOString(),
        finishedAt: endTime.toISOString(),
        answers: userAnswers,
        score
      };
      
      logger.info(`Quiz completed`, {
        quizId,
        score,
        totalQuestions: userAnswers.length,
        duration: duration,
        percentage: Math.round((score / userAnswers.length) * 100),
        isSharedQuiz: isSharedQuiz
      }, 'COMPONENT');
      
      // Save attempt to database
      if (isSharedQuiz && originalUserId) {
        // Save to shared_quizzes collection
        await quizService.saveSharedQuizAttempt(user.uid, originalUserId, quizId, attemptData);
        logger.info('Saved shared quiz attempt', { 
          userId: user.uid, 
          originalUserId, 
          quizId,
          score 
        }, 'COMPONENT');
      } else {
        // Save to regular quizzes collection
        await saveQuizAttempt(userId, quizId, attemptData);
        logger.info('Saved regular quiz attempt', { 
          userId, 
          quizId,
          score 
        }, 'COMPONENT');
      }
      
      setIsCompleted(true);
      
    } catch (err) {
      logger.error('Failed to save quiz attempt', {
        quizId,
        userId,
        originalUserId,
        isSharedQuiz,
        error: err.message,
        stack: err.stack
      }, 'COMPONENT');
      setIsCompleted(true); // Still show results even if saving fails
    }
  };

  if (loading) {
    return <div className="quiz-loading">クイズを読み込み中...</div>;
  }

  if (error) {
    return (
      <div className="quiz-error">
        <p>{error}</p>
        <button onClick={onBack} className="back-btn">戻る</button>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <QuizResults 
        quiz={quiz}
        questions={questions}
        userAnswers={userAnswers}
        onRestart={() => {
          logger.userAction('restart_quiz', { quizId });
          setCurrentQuestionIndex(0);
          setUserAnswers(new Array(questions.length).fill(null));
          setIsCompleted(false);
          setShowingResult(false);
        }}
        onRestartWithSettings={onRestartWithSettings ? () => {
          logger.userAction('restart_quiz_with_settings', { quizId });
          onRestartWithSettings();
        } : null}
        onBack={onBack}
      />
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = userAnswers[currentQuestionIndex];

  // Show result immediately after answering
  if (showingResult && currentAnswer && currentQuestion) {
    return (
      <div ref={questionStartRef}>
        <QuestionResult
          question={currentQuestion}
          userAnswer={currentAnswer}
          shuffledChoices={shuffledChoices[currentQuestion.id] || []}
          onNext={handleNextFromResult}
          isLastQuestion={currentQuestionIndex === questions.length - 1}
          questionNumber={currentQuestionIndex + 1}
          totalQuestions={questions.length}
          onBack={onBack}
        />
      </div>
    );
  }

  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <h1>{quiz.title}</h1>
        <button 
          onClick={onBack}
          className="back-to-list-btn"
          title="クイズ一覧に戻る"
        >
          一覧に戻る
        </button>
        <div className="quiz-progress">
          <span>問題 {currentQuestionIndex + 1} / {questions.length}</span>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div ref={questionStartRef}>
        <QuizQuestion
          question={currentQuestion}
          choices={shuffledChoices[currentQuestion?.id] || []}
          selectedChoices={currentAnswer ? currentAnswer.selectedChoiceIds : []}
          onSubmit={handleAnswer}
        />
      </div>

      <div className="quiz-navigation">
        <button 
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="nav-btn prev-btn"
        >
          前の問題
        </button>
        
        <button 
          onClick={handleNext}
          disabled={!currentAnswer}
          className="nav-btn next-btn"
        >
          {currentQuestionIndex === questions.length - 1 ? '結果を見る' : '次の問題'}
        </button>
      </div>
    </div>
  );
};

export default Quiz;